import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket } from '@fortawesome/free-solid-svg-icons';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import {
  Box, Button, Center, Flex, HStack, Image, Text, Tooltip,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { BurgerMenuItemType } from '../../../api/burger-menu';
import { DrawerMenu } from './DrawerMenu';
import { NotificationsComponent } from '../Notification/Notifications';
import { CourseContext } from '../../../CourseInfoContext';
import { PrivateContext } from '../../../Private.context';
import { useOnline } from '../../../hooks/useOnline';
import { toast } from '../../../utils/toast';
import { useDevice } from '../../../hooks/useDevice';
import { useDefaultStyles } from '../../../hooks/useDefaultStyles';
import { GlobalContext } from '../../../Global.context';
import { RevalidateBadge } from '../RevalidateBadge';
import { HelpButton } from '../HelpButton';

interface HeaderProps {
  burgerItems?: [string, BurgerMenuItemType[]][];
}

export const Header: FC<HeaderProps> = ({ burgerItems, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();
  const onlineCount = useOnline(isOpen);
  const device = useDevice();
  const { colors, logo, sinaremLogo } = useDefaultStyles();

  const handleRocket = useCallback(() => {
    setIsOpen(prev => !prev);
    toast.info({
      title: 'Hora de decolar!',
      description: `${onlineCount} pessoas estudando agora!`,
    });
  }, [onlineCount]);

  const { course, loading, revalidateCourse } = useContext(CourseContext);
  const { profile } = useContext(PrivateContext);
  const { isSprintCourse, isPracticalCourses, isSinaremCourse } = useContext(GlobalContext);

  const userCourse = useMemo(() => {
    if (profile.courses && profile.courses.length > 0 && course && !loading) {
      return course.find(c => c._id === profile.courses[0].course);
    }
    return undefined;
  }, [course, loading, profile.courses]);

  const logoSrc = useMemo(() => {
    if (isSinaremCourse === true) {
      console.log(sinaremLogo);
      return sinaremLogo;
    }

    return logo('logo-header') || '/images/logo-aristo.svg';
  }, [isSinaremCourse, logo, sinaremLogo]);

  const logoW = useMemo(() => {
    if (isSinaremCourse === true) {
      return { base: '130px', md: '146px' };
    }

    return { base: '35px', md: '40px' };
  }, [isSinaremCourse]);

  const headerColor = useMemo(() => {
    if (isSinaremCourse) {
      return colors.background;
    }

    return revalidateCourse !== undefined ? '#141414' : colors.primary.goALittleLighter;
  }, [colors.background, colors.primary.goALittleLighter, isSinaremCourse, revalidateCourse]);

  const iconColor = useMemo(() => {
    if (isSinaremCourse) {
      return colors.black.goInvert;
    }

    return colors.lighter.fromWhite;
  }, [colors.black, colors.lighter.fromWhite, isSinaremCourse]);

  const afterLogo = useMemo(() => {
    if (isSinaremCourse) {
      return null;
    }

    if (revalidateCourse !== undefined) {
      return (
        <RevalidateBadge
          course={revalidateCourse}
          subtitleColor="white"
        />
      );
    }

    return (
      <Tooltip label={userCourse?.title ?? ''}>
        <Text
          color={iconColor}
          noOfLines={2}
          textAlign="left"
          fontSize="sm"
        >
          {userCourse?.title ?? ''}
        </Text>
      </Tooltip>
    );
  }, [iconColor, isSinaremCourse, revalidateCourse, userCourse?.title]);

  return (
    <Flex
      position="relative"
      bg={headerColor}
      as="header"
      w="100%"
      h={{ base: '3.5rem', md: '5rem' }}
      px="1rem"
      borderBottom="1px solid"
      borderColor={colors.lightGray.goAlmostFullDarker}
    >
      <HStack
        position="absolute"
        top={{ base: '4px', md: '14px' }}
        as="button"
        onClick={() => {
          if (isSprintCourse) {
            history.push('/sprint');
          } else if (isPracticalCourses) {
            history.push('/practical-courses');
          } else {
            history.push('/agenda');
          }
        }}
        w={{ base: '20%', md: '30%', lg: '35%', xl: '40%' }}
        align="center"
        h={12}
      >
        <Image w={logoW} src={logoSrc} alt="logo" />
        <Box
          display={{
            base: 'none',
            md: revalidateCourse !== undefined ? 'block' : 'none',
            xl: 'block',
          }}
          w="100%"
        >
          {afterLogo}
        </Box>
      </HStack>
      {device === 'mobile' && isSinaremCourse !== true && (
        <Button
          variant="unstyled"
          borderRadius="50%"
          position="absolute"
          align="center"
          justify="center"
          top={2}
          left={61}
          textAlign="center"
          color={iconColor}
          fontSize="lg"
          border="solid white 1px"
          onClick={handleRocket}
          pr="2px"
          pt="2px"
        >
          <FontAwesomeIcon icon={faRocket} />
        </Button>
      )}
      <Center marginX={{ base: '0px', md: '80px' }} width="100%">
        {children}
      </Center>

      <HStack
        position="absolute"
        top={{ base: '12px', md: '24px' }}
        right={{ base: '22px', md: '22px' }}
        spacing={8}
        ml="1rem"
      >
        <HelpButton />

        <NotificationsComponent iconColor={iconColor} />

        <DrawerMenu items={burgerItems} iconColor={iconColor} />
      </HStack>
    </Flex>
  );
};
